import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Polyline, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/contexts/AuthContext";
import AffiliateStrip from "@/components/AffiliateStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PING_INTERVAL_MS = 5000;          // sample GPS every 5s
const BATCH_INTERVAL_MS = 15000;        // send to server every 15s

function fmtPace(s) {
  if (!s || s <= 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}
function fmtKm(m) {
  if (!m) return "0.00";
  return (m / 1000).toFixed(2);
}
function fmtDuration(sec) {
  if (!sec || sec < 0) return "00:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * GPS Cardio Tracker — Pro scope.
 * - Records sessions via browser Geolocation API
 * - Live OpenStreetMap (Leaflet) route polyline + position marker
 * - Distance + time + pace
 * - Server computes per-km splits on end
 * - Auto-shares to friends feed
 * - 3 PT per km, counts toward cardio_streak
 *
 * Privacy: pings stored only during active session + viewable history.
 * User can delete sessions to scrub. No location data leaves the device
 * until they tap Start.
 */
export default function CardioTracker() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | active | finishing | finished
  const [mode, setMode] = useState("run");
  const [session, setSession] = useState(null);
  const [pings, setPings] = useState([]);
  const [distance, setDistance] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [pace, setPace] = useState(null);
  const [summary, setSummary] = useState(null);
  const [newPRs, setNewPRs] = useState([]);
  const [statsCard, setStatsCard] = useState(null);
  const [permission, setPermission] = useState("prompt"); // prompt | granted | denied
  const [history, setHistory] = useState([]);
  const watchIdRef = useRef(null);
  const sessionStartMsRef = useRef(null);
  const unsentBufferRef = useRef([]);
  const tickIdRef = useRef(null);
  const batchIdRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/cardio/sessions?limit=10`);
      setHistory(data?.items || []);
    } catch {
      setHistory([]);
    }
  }, [user]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/cardio/stats`);
      setStatsCard(data);
    } catch {
      setStatsCard(null);
    }
  }, [user]);

  useEffect(() => { fetchHistory(); fetchStats(); }, [fetchHistory, fetchStats]);

  // Probe permission
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" })
      .then((res) => setPermission(res.state))
      .catch(() => {});
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* ignore */ }
      }
      if (tickIdRef.current) clearInterval(tickIdRef.current);
      if (batchIdRef.current) clearInterval(batchIdRef.current);
    };
  }, []);

  const flushBuffer = useCallback(async (force = false) => {
    if (!session) return;
    const buf = unsentBufferRef.current;
    if (!force && buf.length === 0) return;
    if (buf.length === 0) return;
    const toSend = buf.slice();
    unsentBufferRef.current = [];
    try {
      const { data } = await axios.post(`${API}/cardio/ping`, {
        session_id: session.session_id,
        pings: toSend,
      });
      if (data?.distance_m != null) setDistance(data.distance_m);
      if (data?.duration_sec != null) setElapsed(data.duration_sec);
      if (data?.pace_sec_per_km != null) setPace(data.pace_sec_per_km);
    } catch (e) {
      // Re-queue on failure so we don't drop motion
      unsentBufferRef.current = toSend.concat(unsentBufferRef.current);
    }
  }, [session]);

  async function startSession() {
    if (!("geolocation" in navigator)) {
      toast.error("GPS not supported on this device.");
      return;
    }
    if (!user) {
      toast.error("Sign in to track a run.");
      return;
    }
    setSummary(null);
    setPings([]);
    setDistance(0);
    setElapsed(0);
    setPace(null);
    unsentBufferRef.current = [];
    try {
      const { data } = await axios.post(`${API}/cardio/start`, { mode });
      const s = data?.session;
      if (!s) throw new Error("Failed to start.");
      setSession(s);
      sessionStartMsRef.current = Date.now();
      setStatus("active");
      // Begin GPS watching
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const t = Date.now() - (sessionStartMsRef.current || Date.now());
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, t };
          setPings((prev) => [...prev, p]);
          unsentBufferRef.current.push(p);
          setPermission("granted");
        },
        (err) => {
          if (err?.code === 1) {
            setPermission("denied");
            toast.error("Location access denied. Enable in browser settings.");
          } else {
            // 2 = position unavailable (indoors), 3 = timeout — just keep trying
          }
        },
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 30000 }
      );
      // Local timer for elapsed display + light pace re-calc
      tickIdRef.current = setInterval(() => {
        if (!sessionStartMsRef.current) return;
        setElapsed(Math.floor((Date.now() - sessionStartMsRef.current) / 1000));
      }, 1000);
      // Periodic batch send
      batchIdRef.current = setInterval(() => { flushBuffer(); }, BATCH_INTERVAL_MS);
      toast.success("🏃 Tracking started. Stay safe out there.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start session.");
    }
  }

  async function endSession() {
    if (!session) return;
    setStatus("finishing");
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* ignore */ }
      watchIdRef.current = null;
    }
    if (tickIdRef.current) clearInterval(tickIdRef.current);
    if (batchIdRef.current) clearInterval(batchIdRef.current);
    // Flush any remaining buffered pings
    await flushBuffer(true);
    try {
      const { data } = await axios.post(`${API}/cardio/end`, { session_id: session.session_id });
      setSummary(data?.session || null);
      setNewPRs(data?.prs || []);
      setStatus("finished");
      await refreshUser();
      await fetchHistory();
      await fetchStats();
      const km = (data?.session?.distance_m || 0) / 1000;
      const pt = data?.session?.pt_awarded || 0;
      const prCount = (data?.prs || []).length;
      toast.success(
        `Finished — ${km.toFixed(2)} km · +${pt} Phoenix Tokens 🐦‍🔥${prCount ? ` · ${prCount} NEW PR${prCount > 1 ? "s" : ""}!` : ""}`,
        { duration: 6000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save session.");
      setStatus("active"); // let the user try again
    }
  }

  function resetForNew() {
    setStatus("idle");
    setSession(null);
    setSummary(null);
    setNewPRs([]);
    setPings([]);
    setDistance(0);
    setElapsed(0);
    setPace(null);
  }

  async function deleteHistorySession(id) {
    if (!confirm("Delete this session? This permanently removes the route from your history.")) return;
    try {
      await axios.delete(`${API}/cardio/sessions/${id}`);
      toast.success("Session deleted.");
      await fetchHistory();
    } catch (e) {
      toast.error("Could not delete.");
    }
  }

  // Center the map on the latest ping (or first ping if not active)
  const mapCenter = pings.length > 0
    ? [pings[pings.length - 1].lat, pings[pings.length - 1].lng]
    : [34.0522, -118.2437]; // LA fallback

  const polyline = pings.map((p) => [p.lat, p.lng]);

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="cardio-page">
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              GPS Cardio · Pro
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">
              Track the <span className="text-[#D4AF37]">Distance.</span>
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl">
              Live route mapping · per-km splits · auto-share to your inner circle · earns 3 Phoenix Tokens/km.
            </p>
          </div>
          <Link to="/profile" className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline">
            ← My Profile
          </Link>
        </div>

        {!user && (
          <div className="bg-[#0F0F0F] border border-zinc-900 p-6 text-center">
            <p className="text-neutral-300 mb-3">Sign in to start tracking runs.</p>
          </div>
        )}

        {user && permission === "denied" && status === "idle" && (
          <div data-testid="cardio-perm-denied" className="bg-[#160B22] border border-[#DC2626]/50 p-5 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold mb-1">Location Blocked</div>
            <p className="text-sm text-neutral-300">
              Allow location access in your browser settings to enable GPS tracking. Refresh after granting.
            </p>
          </div>
        )}

        {/* Live tracker — top card */}
        {user && (status === "idle" || status === "finished") && (
          <div data-testid="cardio-start-card" className="bg-[#0F0F0F] border border-zinc-900 p-5 sm:p-6 mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-bold">
                Mode
              </div>
              <div className="flex gap-2" role="radiogroup" aria-label="Cardio mode">
                {["run", "walk", "hike", "ride"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    data-testid={`mode-${m}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border transition-colors ${
                      mode === m
                        ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                        : "bg-transparent text-neutral-400 border-zinc-800 hover:border-[#D4AF37]"
                    }`}
                  >
                    {m === "run" ? "🏃 Run" : m === "walk" ? "🚶 Walk" : m === "hike" ? "🥾 Hike" : "🚴 Ride"}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="cardio-start-btn"
              onClick={startSession}
              disabled={permission === "denied"}
              className="w-full bg-[#DC2626] hover:bg-[#FF3838] disabled:bg-zinc-800 disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.25em] text-sm py-4 transition-colors"
            >
              {status === "finished" ? "Start a New Session" : "Start Tracking"}
            </button>
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-3 text-center">
              Earns 3 Phoenix Tokens per km · Counts toward Phoenix Mover streak · Auto-shares to friends feed
            </div>
          </div>
        )}

        {/* Active session HUD */}
        {user && (status === "active" || status === "finishing") && (
          <div data-testid="cardio-active-hud" className="bg-[#0F0F0F] border-2 border-[#DC2626] p-5 sm:p-6 mb-6">
            <div className="grid grid-cols-3 gap-4 mb-5">
              <Stat label="Distance" value={fmtKm(distance)} unit="km" testId="hud-distance" />
              <Stat label="Time" value={fmtDuration(elapsed)} unit="" testId="hud-time" />
              <Stat label="Pace" value={fmtPace(pace)} unit="/km" testId="hud-pace" />
            </div>
            <button
              data-testid="cardio-end-btn"
              onClick={endSession}
              disabled={status === "finishing"}
              className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.25em] text-sm py-4 transition-colors disabled:opacity-60"
            >
              {status === "finishing" ? "Saving…" : "Finish Session"}
            </button>
          </div>
        )}

        {/* Live map */}
        {user && status !== "idle" && (
          <div data-testid="cardio-map" className="mb-6 border border-zinc-900 overflow-hidden" style={{ height: 360 }}>
            <MapContainer center={mapCenter} zoom={16} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} key={status === "active" ? "live" : "summary"}>
              <TileLayer
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {polyline.length >= 2 && (
                <Polyline positions={polyline} pathOptions={{ color: "#D4AF37", weight: 4 }} />
              )}
              {polyline.length > 0 && (
                <CircleMarker
                  center={polyline[polyline.length - 1]}
                  radius={8}
                  pathOptions={{ color: "#DC2626", weight: 3, fillColor: "#DC2626", fillOpacity: 0.7 }}
                />
              )}
            </MapContainer>
          </div>
        )}

        {/* Summary panel */}
        {user && summary && status === "finished" && (
          <div data-testid="cardio-summary" className="bg-gradient-to-br from-[#160B22] to-[#0A0A0A] border border-[#D4AF37] p-5 sm:p-6 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              Session Complete 🐦‍🔥
            </div>
            {newPRs.length > 0 && (
              <div data-testid="pr-banner" className="mb-4 bg-[#D4AF37]/10 border border-[#D4AF37] p-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
                  🎉 {newPRs.length} New Personal Record{newPRs.length > 1 ? "s" : ""}
                </div>
                <div className="space-y-1">
                  {newPRs.map((pr) => (
                    <div key={pr.key} data-testid={`pr-${pr.key}`} className="text-sm">
                      <span className="text-white font-bold">{pr.label}</span>{" "}
                      <span className="text-neutral-300">— {pr.body}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <Stat label="Distance" value={fmtKm(summary.distance_m)} unit="km" testId="sum-distance" />
              <Stat label="Time" value={fmtDuration(summary.duration_sec)} unit="" testId="sum-time" />
              <Stat label="Pace" value={fmtPace(summary.pace_sec_per_km)} unit="/km" testId="sum-pace" />
              <Stat label="Phoenix Tokens Earned" value={`+${summary.pt_awarded}`} unit="" testId="sum-pt" highlight />
            </div>
            {(summary.splits || []).length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">
                  Per-km Splits
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {summary.splits.map((s) => (
                    <div key={s.km} className="bg-[#0A0A0A] border border-zinc-900 px-2 py-1.5 text-center" data-testid={`split-${s.km}`}>
                      <div className="text-[10px] text-neutral-500 uppercase tracking-widest">KM {s.km}</div>
                      <div className="font-mono tabular-nums text-sm text-white">{fmtPace(s.pace_sec_per_km)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={resetForNew}
              data-testid="cardio-reset-btn"
              className="mt-5 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline"
            >
              Start another →
            </button>
          </div>
        )}

        {/* 🤝 Roc-approved partners — only render in the "finished" state */}
        {user && summary && status === "finished" && (
          <AffiliateStrip
            placement="cardio_finish"
            title="Earned It · Refuel With Roc's Stack"
            variant="carousel"
          />
        )}

        {/* Lifetime stats (PRs) */}
        {user && statsCard && statsCard.session_count > 0 && status !== "active" && status !== "finishing" && (
          <div data-testid="cardio-stats" className="bg-[#0F0F0F] border border-zinc-900 p-5 sm:p-6 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-4">
              Lifetime · Personal Records
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
              <Stat label="Total Distance" value={statsCard.total_distance_km} unit="km" testId="stat-total-km" />
              <Stat label="Longest Run" value={statsCard.longest_distance_km} unit="km" testId="stat-longest-km" />
              <Stat label="Longest Session" value={statsCard.longest_duration_label} unit="" testId="stat-longest-dur" />
              <Stat label="Sessions" value={statsCard.session_count} unit="" testId="stat-sessions" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Best Pace" value={statsCard.best_pace_label} unit="/km" testId="stat-pace-best" />
              <Stat label="Fastest 5K" value={statsCard.fastest_5k_label} unit="/km" testId="stat-pace-5k" />
              <Stat label="Fastest 10K" value={statsCard.fastest_10k_label} unit="/km" testId="stat-pace-10k" />
              <Stat label="Fastest Half" value={statsCard.fastest_half_label} unit="/km" testId="stat-pace-half" />
            </div>
            {statsCard.current_streak > 0 && (
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                🔥 {statsCard.current_streak}-day cardio streak · longest {statsCard.longest_streak}d
              </div>
            )}
          </div>
        )}

        {/* History */}
        {user && history.length > 0 && (
          <div data-testid="cardio-history">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
              Your Recent Sessions
            </div>
            <div className="space-y-2">
              {history.map((s) => (
                <div
                  key={s.session_id}
                  data-testid={`history-${s.session_id}`}
                  className="bg-[#0F0F0F] border border-zinc-900 p-3 sm:p-4 flex items-center justify-between gap-4 flex-wrap"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xl">{s.mode === "run" ? "🏃" : s.mode === "walk" ? "🚶" : s.mode === "hike" ? "🥾" : "🚴"}</span>
                    <div>
                      <div className="font-mono tabular-nums text-sm text-white">
                        {fmtKm(s.distance_m)} km · {fmtDuration(s.duration_sec)} · {fmtPace(s.pace_sec_per_km)}/km
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                        {new Date(s.started_at).toLocaleString()}  ·  +{s.pt_awarded} Phoenix Tokens
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteHistorySession(s.session_id)}
                    data-testid={`history-del-${s.session_id}`}
                    className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#DC2626]"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, unit, testId, highlight }) {
  return (
    <div data-testid={testId} className="text-center">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className={`font-display font-bold text-2xl sm:text-3xl tabular-nums ${highlight ? "text-[#D4AF37]" : "text-white"}`}>
        {value}
        {unit ? <span className="text-xs text-neutral-500 ml-1">{unit}</span> : null}
      </div>
    </div>
  );
}
